import { OutsourceMaterialRow, OutsourceReturnPlanRow } from "./types";

const numberOrDefault = (value: any, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

export const calculateSourceConsumed = (
    outputQuantity: number,
    sourceQuantity: number,
    expectedOutputQuantity: number,
) => {
    if (!expectedOutputQuantity) return 0;
    return outputQuantity / expectedOutputQuantity * sourceQuantity;
};

export const formatConversionRatio = (
    sourceQuantity: number,
    expectedOutputQuantity: number,
    formatNumber: (value: number) => string,
) => {
    if (!sourceQuantity || !expectedOutputQuantity) return "Chưa nhập";
    return `${formatNumber(sourceQuantity)} -> ${formatNumber(expectedOutputQuantity)}`;
};

export const calculateProviderValue = (materials: any[] = []) =>
    normalizeOutsourceMaterials(materials).reduce(
        (total, item) => total + (item.provider_price || 0) * (item.selectedQuantity || 0),
        0,
    );

export const getMaterialId = (item: any) => item?.id || item?.material?.id || item?.material_id;

export const getMaterialName = (item: any, fallback = "Chọn vật tư") =>
    item?.name || item?.material?.name || fallback;

export const getProviderName = (provider: any) => provider?.provider?.name || provider?.name || "";

export const getProviderPrice = (provider: any) => numberOrDefault(provider?.price ?? provider?.provider_price, 0);

export const isSameId = (left?: string | number | null, right?: string | number | null) => {
    if (left === undefined || left === null || right === undefined || right === null) return false;
    return String(left) === String(right);
};

export const getProviderId = (provider: any) => provider?.provider?.id || provider?.provider_id || provider?.id;

export const getOutsourceProviderRelations = (material: any) =>
    Array.isArray(material?.outsource_providers) ? material.outsource_providers : [];

export const getProviderRelation = (material: any, providerId?: string | number | null) => {
    if (!providerId) return undefined;
    return getOutsourceProviderRelations(material).find((provider: any) => isSameId(getProviderId(provider), providerId));
};

export const getFirstProvider = (material: any) => {
    const providers = getOutsourceProviderRelations(material);
    return providers[0] || null;
};

export const materialHasProvider = (material: any, providerId?: string | number | null) => {
    if (!providerId) return true;
    return !!getProviderRelation(material, providerId);
};

export const normalizeMaterialForSelection = (
    material: any,
    providerId?: string | number | null,
    providerName?: string,
): OutsourceMaterialRow => {
    const provider = providerId ? getProviderRelation(material, providerId) : getFirstProvider(material);
    const selectedQuantity = material?.selectedQuantity === undefined || material?.selectedQuantity === null
        ? 1
        : numberOrDefault(material.selectedQuantity, 1);
    return {
        ...material,
        selectedQuantity,
        provider_name: providerId
            ? getProviderName(provider) || providerName || material?.provider_name || ""
            : material?.provider_name || getProviderName(provider),
        provider_price:
            providerId && provider
                ? getProviderPrice(provider)
                : material?.provider_price !== undefined
                ? numberOrDefault(material.provider_price, 0)
                : getProviderPrice(provider),
        providers: getOutsourceProviderRelations(material),
        outsource_providers: getOutsourceProviderRelations(material),
    };
};

export const normalizeOutsourceMaterial = (item: any): OutsourceMaterialRow => {
    const material = item?.material ? item.material : item;
    const selectedQuantity = item?.material
        ? item.quantity
        : item?.selectedQuantity ?? item?.quantity;

    return {
        ...material,
        selectedQuantity: numberOrDefault(selectedQuantity, 0),
        outsource_standards: Array.isArray(item?.outsource_standards)
            ? item.outsource_standards
            : material?.outsource_standards || [],
        provider_name: item?.provider_name || material?.provider_name || "",
        provider_price: numberOrDefault(item?.provider_price ?? material?.provider_price, 0),
        providers: getOutsourceProviderRelations(material),
        outsource_providers: getOutsourceProviderRelations(material),
    };
};

export const normalizeOutsourceMaterials = (items: any[] = []) =>
    (Array.isArray(items) ? items : []).map(normalizeOutsourceMaterial);

export const resolveOutsourceMaterialsWithCatalog = (
    materials: any[] = [],
    catalogMaterials: any[] = [],
) => materials.map((material: any) => {
    const catalogMaterial = catalogMaterials.find(
        (candidate: any) => String(candidate.id) === String(material.id),
    );

    return {
        ...material,
        outsource_standards:
            catalogMaterial?.outsource_standards || material.outsource_standards || [],
    };
});

export const hasOutsourceSourceMaterialShortage = (materials: any[] = []) =>
    materials.some((material: any) => {
        const outputQuantity = numberOrDefault(material.selectedQuantity, 0);
        const standards = Array.isArray(material.outsource_standards)
            ? material.outsource_standards
            : [];

        return standards.some((standard: any) => {
            const requiredQuantity = Number((
                outputQuantity * numberOrDefault(standard.source_quantity_per_output, 0)
            ).toFixed(6));
            return requiredQuantity > numberOrDefault(standard.quantity, 0);
        });
    });

const makeLocalPlanId = (plan: any, index: number) =>
    plan?.localId || plan?.id || `plan-${index}`;

export const normalizeReturnPlan = (
    plan: any,
    index = 0,
    sourceMaterials: any[] = [],
): OutsourceReturnPlanRow => {
    const sourceMaterialId = plan?.source_material_id || getMaterialId(plan?.source_material);
    const sourceMaterial =
        plan?.source_material ||
        sourceMaterials.find((material) => getMaterialId(material) === sourceMaterialId) ||
        null;
    const outputMaterialId = plan?.output_material_id || getMaterialId(plan?.output_material);
    const outputMaterial = plan?.output_material || null;
    const expectedOutputQuantity = numberOrDefault(plan?.expected_output_quantity, 0);

    return {
        ...plan,
        id: plan?.id,
        localId: makeLocalPlanId(plan, index),
        source_material: sourceMaterial,
        source_material_id: sourceMaterialId || null,
        output_material: outputMaterial,
        output_material_id: outputMaterialId || null,
        source_quantity: numberOrDefault(plan?.source_quantity, 0),
        expected_output_quantity: expectedOutputQuantity,
        note: plan?.note || "",
        lines: Array.isArray(plan?.lines)
            ? plan.lines.map((line: any) => ({
                  ...line,
                  material_id: line?.material_id || getMaterialId(line?.material) || outputMaterialId,
                  expected_quantity: numberOrDefault(line?.expected_quantity, expectedOutputQuantity),
                  note: line?.note || "",
              }))
            : [],
    };
};

export const normalizeReturnPlans = (plans: any[] = [], sourceMaterials: any[] = []) =>
    (Array.isArray(plans) ? plans : []).map((plan, index) =>
        normalizeReturnPlan(plan, index, sourceMaterials),
    );

export const createReturnPlan = (sourceMaterial: any): OutsourceReturnPlanRow => ({
    localId: `new-${Date.now()}`,
    source_material: sourceMaterial || null,
    source_material_id: getMaterialId(sourceMaterial) || null,
    output_material: null,
    output_material_id: null,
    source_quantity: 1,
    expected_output_quantity: 1,
    note: "",
    lines: [],
});

export const filterReturnPlansBySourceMaterials = (plans: any[] = [], materials: any[] = []) => {
    const materialIds = new Set(materials.map((material) => getMaterialId(material)).filter(Boolean));
    return (Array.isArray(plans) ? plans : []).filter((plan) => {
        const sourceId = plan?.source_material_id || getMaterialId(plan?.source_material);
        return sourceId && materialIds.has(sourceId);
    });
};

export const serializeOutsourceMaterials = (materials: any[] = []) =>
    normalizeOutsourceMaterials(materials).map((material) => ({
        material_id: material.id,
        quantity: material.selectedQuantity,
        provider_name: material.provider_name || "",
        provider_price: material.provider_price || 0,
    }));

export const serializeStorageKeeperIds = (storageKeepers: any): string[] =>
    (Array.isArray(storageKeepers) ? storageKeepers : storageKeepers ? [storageKeepers] : [])
        .map((storageKeeper: any) => {
            if (typeof storageKeeper === "string") return storageKeeper;
            return storageKeeper?.id || storageKeeper?.user_id || storageKeeper?.user?.id;
        })
        .filter(Boolean);

const isPersistedPlanId = (id?: string) =>
    Boolean(id && !String(id).startsWith("new-") && !String(id).startsWith("plan-"));

export const serializeReturnPlans = (plans: any[] = [], sourceMaterials: any[] = []) =>
    normalizeReturnPlans(plans, sourceMaterials).map((plan) => {
        const outputMaterialId = plan.output_material_id || getMaterialId(plan.output_material);
        const sourceMaterialId = plan.source_material_id || getMaterialId(plan.source_material);
        const payload: any = {
            source_material_id: sourceMaterialId,
            output_material_id: outputMaterialId,
            source_quantity: plan.source_quantity,
            expected_output_quantity: plan.expected_output_quantity,
            note: plan.note || "",
            lines: (plan.lines?.length
                ? plan.lines
                : [{ material_id: outputMaterialId, expected_quantity: plan.expected_output_quantity, note: "" }]
            ).map((line: any) => ({
                material_id: line.material_id || getMaterialId(line.material) || outputMaterialId,
                expected_quantity: line.expected_quantity || plan.expected_output_quantity,
                note: line.note || "",
            })),
        };

        if (isPersistedPlanId(plan.id)) {
            payload.id = plan.id;
        }

        return payload;
    });

export const hasInvalidReturnPlan = (plan: any) =>
    !plan.source_material_id ||
    !plan.output_material_id ||
    !Number.isFinite(Number(plan.source_quantity)) ||
    Number(plan.source_quantity) <= 0 ||
    !Number.isFinite(Number(plan.expected_output_quantity)) ||
    Number(plan.expected_output_quantity) <= 0;

export const isValidReturnPlan = (plan: any) => !hasInvalidReturnPlan(normalizeReturnPlan(plan));
